import React, { useState, useRef, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import Icon from 'components/Icon'
import DemoPlayer from '../DemoPlayer'
import debounce from 'debounce-promise'
import useCurrentUser from 'hooks/useCurrentUser'
import { Select, SelectAsync, Card, Input, Button, Banner } from '@arnsbogroup/react-voicearchive'
import { Link } from 'react-router-dom'
import useValidate, { is } from '@arnsbogroup/react-voicearchive/hooks/useValidate'

const DemoInlineEditor = ({ supplierId, file, demo, onAdded, onChange, onCancel }) => {
  const { roles = [] } = useCurrentUser()
  const { formatMessage } = useIntl()
  const [error, setError] = useState(null)
  const [options, setOptions] = useState([])
  const [product, setProduct] = useState(demo && demo.product ? { label: demo.product, value: demo.product_uid } : null)
  const [type, setType] = useState(demo && demo.type ? { label: demo.type, value: demo.type_uid } : null)
  const [language, setLanguage] = useState(demo && demo.language ? { label: demo.language, value: demo.language_uid } : null)
  const [name, setName] = useState(demo ? demo.name : null)
  const [dialect, setDialect] = useState(demo && demo.dialect_uid ? { label: demo.dialect, value: demo.dialect_uid } : null)
  const [accent, setAccent] = useState(demo && demo.accent_uid ? { label: demo.accent, value: demo.accent_uid } : null)
  const [loading, setLoading] = useState(false)
  const [supplierPromoPriorityIds, setSupplierPromoPriorityIds] = useState(demo && demo.supplierPromoPriorityIds ? demo.supplierPromoPriorityIds.map((priority) => ({value: priority.id, label: priority.name})) : null)
  const isCoordinator = roles.includes("coordinator")
  const [validations, validate] = isCoordinator
  ? useValidate({ language: language, name: name }, { language: [is.present], name: [is.present] }, true)
  : useValidate({
      language: language,
      product: product,
      name: name
    }, {
      language: [is.present],
      product: [is.present],
      name: [is.present]
    }, true)

  const i18n = (errorMessages) => errorMessages?.map((errorMessage) => ({
    present: formatMessage(messages.errorsPresent)
  }[errorMessage] || errorMessage))

  useEffect(() => {
    fetch(`/rest/v2/supplier/${supplierId}/demo-options`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ data }) => setOptions(data.options))
      .catch((err) => {
        window.alert(err)
        console.error(err)
      })
  }, [])

  const onCreate = () => {
    if (!validate()) {
      return
    }
    setLoading(true)

    const body = new FormData()
    body.append('addFileFile', file)
    body.append('supplierid', supplierId)
    body.append('product_uid', product ? product.value : null)
    body.append('language_uid', language ? language.value : null)
    body.append('dialect_uid', dialect ? dialect.value : null)
    body.append('accent_uid', accent ? accent.value : null)
    body.append('name', name)
    // body.append('voiceage_uid', '')
    body.append('voicekind_uid', '')

    if (type) {
      body.append('filetype', type.value) // Legacy promo type UID
      body.append('hide', true)
    } else {
      body.append('filetype', 0)
    }
    
    if (supplierPromoPriorityIds) {
      body.append('supplierPromoPriorityIds', supplierPromoPriorityIds.map((priority) => {
        return priority.value;
      }))
    } else {
      body.append('supplierPromoPriorityIds', 0)
    }

    fetch(`/supplier/addpromo.json`, { method: 'POST', body, credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ success, errorMsg, file: nextDemo }) => {
        setLoading(false)

        if (!success) {
          setError(errorMsg)
        } else if (onAdded) {
          onAdded({
            ...nextDemo,
            product: product ? product.label : null,
            language: language ? language.label : null,
            type: type ? type.label : null,
            cdn_url: URL.createObjectURL(file),
            active: type ? false : true,
            supplierPromoPriorityIds: supplierPromoPriorityIds ? supplierPromoPriorityIds : null,
            dialect: dialect ? dialect.label : null,
            accent: accent ? accent.label : null,
          })
        }
      })
      .catch((err) => {
        window.alert(err)
        console.error(err)
      })
  }

  const onTypeChange = (nextValue, { action }) => {
    if (nextValue.value != 0) {
      setProduct(null)
    }

    if (action == 'create-option') {
      fetch(`/rest/v2/supplierpromotype`, {
        credentials: 'same-origin',
        method: 'POST',
        body: JSON.stringify({ name: nextValue.label }),
      })
      .then((response) => response.json())
      .then(({ uid, name }) => {
        setOptions((options) => ([ ...options, { type: 'type', uid, name } ]))
        setType({ value: uid, label: name })
      })
    } else {
      setType(nextValue)
    }
  }

  const onUpdate = () => {
    if (!validate()) {
      return
    }
    setLoading(true)

    fetch(`/rest/v2/supplier/${supplierId}/demos/${demo.uid}`, {
      credentials: 'same-origin',
      method: 'PATCH',
      body: JSON.stringify({
        language_uid: language ? language.value : null,
        product_uid: product ? product.value : null,
        type_uid: type ? type.value : null,
        dialect_uid: dialect ? dialect.value : null,
        accent_uid: accent ? accent.value : null,
        name,
        supplierPromoPriorityIds: supplierPromoPriorityIds ? supplierPromoPriorityIds.map((priority) => {
          return priority.value;
        }) : null
      })
    })
      .then((response) => response.json())
      .then(({ success, errors }) => {
        setLoading(false)

        if (!success) {
          window.alert(errors.join(' '))
        } else if (onChange) {
          onChange({
            ...demo,
            name,
            product: product ? product.label : '',
            language: language ? language.label : '',
            type: type ? type.label : '',
            supplierPromoPriorityIds: supplierPromoPriorityIds ? supplierPromoPriorityIds : '',
            dialect: dialect ? dialect.label : '',
            accent: accent ? accent.label : ''
          })
        }
      })
      .catch((err) => {
        window.alert(err)
        console.error(err)
      })
  }

  const onSave = file ? onCreate : onUpdate

  const src = useMemo(() => {
    if (file) {
      return URL.createObjectURL(file)
    }

    return demo?.cdn_url
  }, [file, demo])

  const onSupplierPromoPrioritiesChange = (priorities) => {
    if (!priorities || typeof priorities[0] === 'undefined') {
      setSupplierPromoPriorityIds(null)
    } else {
      setSupplierPromoPriorityIds(priorities)
    }
  }

  return (
    <Card className="bg-gray-100 px-4 py-5 space-y-4">
      { error &&
        <Banner
          state="error"
          subtitle={error}
          onClose={() => setError(null)}
        >
          <Link to="/docs/Demo%20Specifications">
            <Button state="negative" variant="plain" size="sm">
              <Icon icon="life-ring" /> <FormattedMessage {...messages.errorHelp } />
            </Button>
          </Link>
        </Banner>
      }
      <div className="flex items-center space-x-2">
        <div className="w-1/12">
          <DemoPlayer src={src} />
        </div>
        <div className="w-8/12 text-sm space-y-2 pr-10 ">
          <Input
            required
            label={formatMessage(messages.nameLabel)}
            value={name}
            name="name"
            onChange={({ target: { value } }) => setName(value)}
            placeholder={formatMessage(messages.namePlaceholder)}
            className="w-full"
            errors={i18n(validations.name)}
          />

          <div className="grid grid-cols-2 gap-2">
            <Select
              label={formatMessage(messages.productLabel)}
              required={!isCoordinator}
              className="bg-white"
              name="product"
              openMenuOnFocus
              options={options.filter(({ type }) => type === 'product').map(({ uid, name }) => ({ label: name, value: uid }))}
              value={product}
              onChange={setProduct}
              noOptionsMessage={({ inputValue }) => formatMessage(messages.noOption, { query: inputValue })}
              placeholder={formatMessage(messages.productPlaceholder)}
              isDisabled={!!type && type.value != 0}
              errors={i18n(validations.product)}
            />

            <Select
              label={formatMessage(messages.languageLabel)}
              required
              className="bg-white"
              name="language"
              openMenuOnFocus
              options={options.filter(({ type }) => type === 'language').map(({ uid, name }) => ({ label: name, value: uid }))}
              value={language}
              onChange={setLanguage}
              noOptionsMessage={({ inputValue }) => formatMessage(messages.noOption, { query: inputValue })}
              placeholder={formatMessage(messages.languagePlaceholder)}
              errors={i18n(validations.language)}
            />

            { options.filter(({ type }) => type === 'type').length > 0 && isCoordinator &&
              <>
                  <Select
                    className="bg-white"
                    name="type"
                    openMenuOnFocus
                    options={options.filter(({ type }) => type === 'type').map(({ uid, name }) => ({ label: name, value: uid }))}
                    value={type}
                    onChange={onTypeChange}
                    noOptionsMessage={({ inputValue }) => formatMessage(messages.noOption, { query: inputValue })}
                    placeholder="Type"
                  />
                
                  <SelectAsync
                    isMulti
                    className="bg-white"
                    value={supplierPromoPriorityIds}
                    onChange={onSupplierPromoPrioritiesChange}
                    placeholder="Priorities"
                    noOptionsMessage={({ inputValue }) => `No priority found for '${inputValue}'`}
                    loadOptions={
                      debounce((query) => fetch(`/rest/v2/supplierpriority?search[name]=${query}&search[hidden]=0&search[deleted]=0`)
                        .then((response) => response.json())
                        .then((priorities) => priorities.map(({ uid, name }) => ({ value: uid, label: name })))
                      , 250)
                    }
                  />
              </>
            }
            <SelectAsync
              name="accent"
              className="w-1/2 bg-white"
              placeholder={formatMessage(messages.accentPlaceholder)}
              value={accent}
              onChange={setAccent}
              options={[]}
              noOptionsMessage={({ inputValue }) => formatMessage(messages.noOption, { query: inputValue })}
              cacheOptions
              defaultOptions
              isClearable
              loadOptions={
                  debounce((query) => fetch(`/rest/v2/language?search[name]=${query}`)
                    .then((response) => response.json())
                    .then((result) => result.map(({ uid, name }) => ({ value: uid, label: name })))
                  , 250)
              }
            />
            { language && options.some(({type, data}) => type === 'dialect' && data && language.value == data.languageId) &&
              <Select
                name="dialect"
                className="w-1/2 bg-white"
                placeholder={formatMessage(messages.dialectPlaceholder)}
                options={
                  options
                    .filter(({ type, data }) => type === 'dialect' && data && language.value == data.languageId)
                    .map(({ uid, name, ...rest }) => ({ label: name, value: uid, ...rest }))
                }
                value={dialect}
                onChange={setDialect}
              />
            }
          </div>
        </div>
        <div className="w-3/12 flex justify-between items-center text-sm">
          <div className="rounded-full w-2 h-2 bg-yellow-400" />

          <Button
            variant="inline"
            size="sm" 
            onClick={loading ? undefined : onSave}
          >
            <FormattedMessage {...messages.saveLabel} />
          </Button>

          <Button 
            variant="inline"
            state="negative"
            size="sm"
          >
            <FormattedMessage {...messages.deleteLabel} />
          </Button>

          { loading ? (
            <Icon icon={['fas', 'spinner-third']} className="animate-spin cursor-pointer text-gray-400" />
          ) : (
            <Icon icon={['fas', 'undo']} className="text-gray-400 cursor-pointer" onClick={onCancel} />
          ) }
        </div>
      </div>
    </Card>
  )
}

export default DemoInlineEditor
